import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getOrganizationSchema, getWebPageSchema } from '@/components/base/SEOHead';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const orgLD = document.createElement('script');
    orgLD.type = 'application/ld+json';
    orgLD.textContent = JSON.stringify(getOrganizationSchema(), null, 2);
    document.head.appendChild(orgLD);

    const webpageLD = document.createElement('script');
    webpageLD.type = 'application/ld+json';
    webpageLD.textContent = JSON.stringify(
      getWebPageSchema(
        '关于我们 - 华体会官网',
        '了解华体会官网的品牌故事、平台信誉和联系方式。华体会官网是您体验顶级体育娱乐的首选平台。',
        'https://www.huatiyuiguanwang.com/about'
      ),
      null,
      2
    );
    document.head.appendChild(webpageLD);

    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
        if (el.textContent?.includes('Organization') || el.textContent?.includes('WebPage')) {
          el.remove();
        }
      });
    };
  }, []);

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setContactStatus('success');
      form.reset();
      return;
    }
    const submitUrl = 'https://readdy.ai/api/form/d9qt6aknlsngrm4lm5l0';
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        params.append(key, value as string);
      }
    });
    fetch(submitUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        if (res.ok && parsed?.code === 'OK') {
          setContactStatus('success');
          form.reset();
        } else {
          setContactStatus('error');
          setContactError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试');
        }
      })
      .catch(() => {
        setContactStatus('error');
        setContactError('网络错误，请稍后再试');
      });
  };

  const milestones = [
    { year: '2018', title: '平台创立', desc: '华体会官网正式上线，以安全可靠的体育竞猜服务进入市场。' },
    { year: '2019', title: '赛事全覆盖', desc: '完成足球、篮球、网球等主流体育项目全覆盖，用户突破百万。' },
    { year: '2020', title: '电竞板块上线', desc: '率先推出电竞竞猜板块，覆盖LOL、DOTA2等热门电竞项目。' },
    { year: '2021', title: '国际化拓展', desc: '支持多语言和多币种，服务全球体育爱好者。' },
    { year: '2022', title: '技术创新', desc: '升级安全防护体系，引入AI智能赔率分析系统。' },
    { year: '2024', title: '会员体系升级', desc: '全新VIP会员体系上线，为用户提供更优质的专属服务。' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于华体会官网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              华体会官网致力于为全球体育爱好者打造安全、公平、便捷的顶级体育娱乐平台。我们汇聚海量赛事资源，以专业的技术和服务，为每一位用户带来极致的体育竞猜体验。
            </p>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    华体会官网自成立以来，始终秉持"安全第一、用户至上"的运营理念，致力于为全球体育爱好者提供最优质的在线体育娱乐服务。我们深知安全和信任是体育竞猜平台的基石，因此投入大量资源建设国际领先的安全防护体系。
                  </p>
                  <p>
                    华体会官网汇聚了全球顶级体育赛事的实时数据，涵盖足球、篮球、网球、电竞等数十种体育项目，提供专业的赔率分析和丰富的竞猜玩法。无论您是资深体育迷还是新手玩家，都能在华体会官网找到适合自己的竞猜方式。
                  </p>
                  <p>
                    我们的团队由资深体育分析师、技术专家和客服精英组成，以专业的态度和高效的服务，保障每一位用户的体验。华体会官网全天候客服7×24小时在线，随时解答您的疑问，处理您的需求。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20modern%20office%20environment%20with%20warm%20lighting%2C%20collaborative%20team%20atmosphere%2C%20sports%20brand%20headquarters%20style%2C%20clean%20aesthetic%2C%20soft%20shadows%2C%20editorial%20photography%2C%20comfortable%20and%20inviting%20mood%2C%20neutral%20warm%20tones&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="华体会官网品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 items-start ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                      <div className="md:hidden absolute left-0 w-8 h-8 flex items-center justify-center">
                        <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
                      </div>
                      <span className="text-accent-700 font-heading font-bold text-lg">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600 mt-1">{item.desc}</p>
                    </div>
                    <div className="hidden md:flex w-8 shrink-0 items-center justify-center">
                      <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              平台信誉
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '正规牌照', desc: '持有国际认可运营牌照' },
                { icon: 'ri-lock-line', title: 'SSL加密', desc: '银行级数据加密保护' },
                { icon: 'ri-auction-line', title: '公平认证', desc: '国际随机算法认证' },
                { icon: 'ri-hand-heart-line', title: '责任博彩', desc: '倡导健康竞猜理念' },
              ].map((item) => (
                <div key={item.title} className="text-center bg-background-50 rounded-lg p-6 border border-background-200/60">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-3">
                    <i className={`${item.icon} text-accent-700 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">联系方式</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                      <i className="ri-customer-service-2-line text-accent-700"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-900">7×24小时客服热线</span>
                      <p className="text-sm text-foreground-600 mt-0.5">400-000-0000</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                      <i className="ri-mail-line text-accent-700"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-900">电子邮箱</span>
                      <p className="text-sm text-foreground-600 mt-0.5">service@huatiyuiguanwang.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                      <i className="ri-wechat-line text-accent-700"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-900">微信客服</span>
                      <p className="text-sm text-foreground-600 mt-0.5">华体会官方客服</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                      <i className="ri-time-line text-accent-700"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-900">服务时间</span>
                      <p className="text-sm text-foreground-600 mt-0.5">全年无休 · 全天候24小时在线</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">在线留言</h3>
                <form data-readdy-form id="contact-form" onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-400 transition-colors duration-200"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-400 transition-colors duration-200"
                    />
                  </div>
                  <input
                    type="text"
                    name="subject"
                    placeholder="主题"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-400 transition-colors duration-200"
                  />
                  <textarea
                    name="message"
                    placeholder="请描述您的问题或建议（500字以内）"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-400 transition-colors duration-200 resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot"
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-2.5 bg-accent-500 text-background-50 text-sm font-semibold rounded-md hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
                {contactStatus === 'success' && (
                  <p className="mt-3 text-sm text-primary-700 font-medium">留言发送成功！我们会尽快与您联系。</p>
                )}
                {contactStatus === 'error' && (
                  <p className="mt-3 text-sm text-accent-700 font-medium">{contactError}</p>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}